import { Injectable } from '@angular/core';
import { Observable } from 'rxjs/Observable';
import { HttpHeaders, HttpClient } from '@angular/common/http';
import { DataForDisplayingMenuItemsService } from './data-for-displaying-menu-items.service';

import { ToasterService } from 'angular2-toaster';
import { environment } from '../../environments/environment';
import { RailingColorsService } from './railing/services/railing-colors.service';
import { ProductTypes } from '../models/2d3d/product-types';
import { AwningService } from './awning/awning.service';
import { AwningTypes } from './awning/models/awning-types';
import { AuthService } from './auth.service';
import { mergeGenericDoorsWindows } from '../models/2d3d/doors-windows.config';
import { mergeLocalTrellis } from '../models/2d3d/trellis.config';
import { mergeLocalKellyPanels } from '../models/2d3d/laser-cut-kelly-local.config';
import { mergeLocalDockRailKit } from '../models/2d3d/dockrail-local.config';
import { mergeLocalDockProps } from '../models/2d3d/dock-props-local.config';
import { mergeLocalModernKit } from '../models/2d3d/modern-local.config';

const httpOptions = {
  headers: new HttpHeaders({ 'Content-Type': 'application/json' })
};

@Injectable()
export class ProductsService {
  private _URL: string = environment.apiUrl;
  private _products: Array<any> = [];
  private _accessories: Array<any> = [];
  private _awning: Array<any> = [];
  private _doorsAndWindows: Array<any> = [];
  private _currentParams: any = {};
  private _productsObservable: Observable<any>;
  private _doorsWindows: any[] = [];
  doorsWindowsPromise: Promise<any>;
  shareProjPromise: Promise<any>;

  constructor(
    private http: HttpClient,
    private dataMenuService: DataForDisplayingMenuItemsService,
    private toasterService: ToasterService,
    private railingColorsService: RailingColorsService,
    private awningService: AwningService,
    private authService: AuthService
  ) {
    this._productsObservable = this.getProducts();

    this._productsObservable.subscribe(prods => {
      this._products = mergeLocalModernKit(
        mergeLocalDockProps(mergeLocalDockRailKit(mergeLocalKellyPanels(prods)))
      );
      // console.log('prods', prods)
    });

    this.shareProjPromise = new Promise<void>((resolve, reject) => {
      this._productsObservable.subscribe(prods => {
        this.railingColorsService.getColors().subscribe(colors => {
          this.railingColorsService.setColors(colors);
          resolve();
        });
        //resolve();
      });
    });
  }

  getProducts(): Observable<any> {
    return this.http.get(`${this._URL}/products`, httpOptions);
  }

  // Add new product to API
  addProduct(product: any): Observable<any> {
    const token = this.authService.getToken();
    console.log('token', token);
    console.log('product', product);
    
    if (!token) {
      return new Observable(observer => {
        observer.error(new Error('No authentication token available. Please log in.'));
      });
    }
    
    const authHttpOptions = {
      headers: new HttpHeaders({ 
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      })
    };
    return this.http.post(`${this._URL}/products`, product, authHttpOptions);
  }

  // Update existing product
  updateProduct(id: string, product: any): Observable<any> {
    const token = this.authService.getToken();
    
    if (!token) {
      return new Observable(observer => {
        observer.error(new Error('No authentication token available. Please log in.'));
      });
    }
    
    const authHttpOptions = {
      headers: new HttpHeaders({ 
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      })
    };
    return this.http.put(`${this._URL}/products/${id}`, product, authHttpOptions);
  }

  // Delete product
  deleteProduct(id: string): Observable<any> {
    const token = this.authService.getToken();
    
    if (!token) {
      return new Observable(observer => {
        observer.error(new Error('No authentication token available. Please log in.'));
      });
    }
    
    const authHttpOptions = {
      headers: new HttpHeaders({ 
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      })
    };
    return this.http.delete(`${this._URL}/products/${id}`, authHttpOptions);
  }

  showToast(type: string, title: string, body: string) {
    this.toasterService.popAsync(type, title, body);
  }

  getCurrentIntermidiatePost(height?): any {
    const heightValue = height ? height : this._currentParams.railingHeight;
    this._currentParams = this.dataMenuService.getCurrentParams();

    for (let i = 0; i < this._products.length; i++) {
      if (
        this._products[i].railingType &&
        this._products[i].railingType.includes(this._currentParams.railingType) &&
        this._products[i].productType === 'Intermediate Post' &&
        this._products[i].mountType === this._currentParams.postMounting &&
        this._products[i].heightGroup === heightValue
      ) {
        return this._products[i];
      }
    }
    const error = `railing type': ${this._currentParams.railingType},
      'mount type': ${this._currentParams.postMounting},
      'height': ${heightValue}`;

    this.showToast('error', 'IntermidiatePost product is missing', error);
  }

  getCurrentIntermediateAngledPost(height?): any {
    const heightValue = height ? height : this._currentParams.railingHeight;
    this._currentParams = this.dataMenuService.getCurrentParams();

    for (let i = 0; i < this._products.length; i++) {
      if (
        this._products[i].railingType &&
        this._products[i].railingType.includes(this._currentParams.railingType) &&
        this._products[i].productType === 'Intermediate Angled Post' &&
        this._products[i].mountType === this._currentParams.postMounting &&
        this._products[i].heightGroup === heightValue
      ) {
        return this._products[i];
      }
    }
    const error = `railing type': ${this._currentParams.railingType},
      'mount type': ${this._currentParams.postMounting},
      'height': ${heightValue}`;

    this.showToast('error', 'IntermidiateAngledPost product is missing', error);
  }

  getCurrentCornerPost(height?): any {
    this._currentParams = this.dataMenuService.getCurrentParams();
    const heightValue = height ? height : this._currentParams.railingHeight;

    for (let i = 0; i < this._products.length; i++) {
      if (
        this._products[i].railingType &&
        this._products[i].railingType.includes(this._currentParams.railingType) &&
        this._products[i].productType === 'Corner Post' &&
        this._products[i].mountType === this._currentParams.postMounting &&
        this._products[i].heightGroup === heightValue &&
        this._products[i].cornerType === this._currentParams.cornerPosts &&
        this._products[i].angleType === 'Outside'
      ) {
        return this._products[i];
      }
    }
    const error = `railing type': ${this._currentParams.railingType},
      'mount type': ${this._currentParams.postMounting},
      'height': ${heightValue},
      'corner post': ${this._currentParams.cornerPosts}`;

    this.showToast('error', 'Corner post product is missing', error);
  }

  getCurrentInsideCornerPost(): any {
    this._currentParams = this.dataMenuService.getCurrentParams();

    for (let i = 0; i < this._products.length; i++) {
      if (
        this._products[i].railingType &&
        this._products[i].railingType.includes(this._currentParams.railingType) &&
        this._products[i].productType === 'Corner Post' &&
        this._products[i].mountType === this._currentParams.postMounting &&
        this._products[i].heightGroup === this._currentParams.railingHeight &&
        this._products[i].cornerType === this._currentParams.cornerPosts &&
        this._products[i].angleType === 'Inside'
      ) {
        return this._products[i];
      }
    }
    const error = `railing type': ${this._currentParams.railingType},
      'mount type': ${this._currentParams.postMounting},
      'height': ${this._currentParams.railingHeight},
      'corner post': ${this._currentParams.cornerPosts}`;

    this.showToast('error', 'Inside corner post product is missing', error);
  }

  getCurrentHandrail(height?, selectedType?): any {
    this._currentParams = this.dataMenuService.getCurrentParams();
    const heightValue = height ? height : this._currentParams.railingHeight;
    const type = selectedType ? selectedType : this._currentParams.topRail;
    let error;

    if (type === 'CompositeTop') {
      for (let i = 0; i < this._products.length; i++) {
        if (
          this._products[i].placementRule === 'Continuous' &&
          this._products[i].handrailType === 'Composite' &&
          this._products[i].heightGroup === heightValue
        ) {
          return this._products[i];
        }
      }
      error = `railing type: ${type},
      handrail type: Composite,
      placement rule: Continuous
      height: ${heightValue}`;
    } else if (type === 'CompositeBetween') {
      for (let i = 0; i < this._products.length; i++) {
        if (
          this._products[i].placementRule === 'Between Posts' &&
          this._products[i].handrailType === 'Composite' &&
          this._products[i].heightGroup === heightValue
        ) {
          return this._products[i];
        }
      }
      error = `railing type: ${type},
      handrail type: Composite,
      placement rule: Between Posts
      height: ${heightValue}`;
    } else {
      for (let i = 0; i < this._products.length; i++) {
        if (
          this._products[i].railingType &&
          this._products[i].railingType.includes(this._currentParams.railingType) &&
          this._products[i].productType === 'Handrail' &&
          this._products[i].handrailType === type &&
          this._products[i].heightGroup === heightValue
        ) {
          return this._products[i];
        }
      }
      let handrailType;
      switch (type) {
        case '100s':
          {
            handrailType = 'DR-Square';
          }
          break;
        case '150s':
          {
            handrailType = 'DR-Graspable';
          }
          break;
        case '200s':
          {
            handrailType = 'DR-Rectangular';
          }
          break;
        case '300s':
          {
            handrailType = 'DR-Round';
          }
          break;
        case '350s':
          {
            handrailType = 'DR-Elliptical';
          }
          break;
        default: {
          handrailType = type;
        }
      }
      error = `railing type: ${this._currentParams.railingType},
        handrail type: ${handrailType},
        height: ${heightValue}`;
    }

    this.showToast('error', 'Handrail product is missing', error);
  }

  getCurrentBaserail(): any {
    this._currentParams = this.dataMenuService.getCurrentParams();

    for (let i = 0; i < this._products.length; i++) {
      if (
        this._products[i].railingType &&
        this._products[i].railingType.includes(this._currentParams.railingType) &&
        this._products[i].productType === 'BaseRail'
      ) {
        return this._products[i];
      }
    }
    const error = `railing type': ${this._currentParams.railingType}`;
    this.showToast('error', 'Base rail product is missing', error);
  }

  getCurrentHorizontalCable(height?): any {
    this._currentParams = this.dataMenuService.getCurrentParams();
    const heightValue = height ? height : this._currentParams.railingHeight;
    for (let i = 0; i < this._products.length; i++) {
      if (
        this._products[i].railingType &&
        this._products[i].railingType.includes(this._currentParams.railingType) &&
        this._products[i].productType === 'Horizontal Cable' &&
        this._products[i].heightGroup === heightValue
      ) {
        return this._products[i];
      }
    }
    const error = `railing type: ${this._currentParams.railingType},
      height: ${heightValue}`;

    this.showToast('error', 'Horizontal cable product is missing', error);
  }

  getCurrentHorizontalCableForDoubleCornerWhithoutBaserail(height?): any {
    this._currentParams = this.dataMenuService.getCurrentParams();
    const heightValue = height ? height : this._currentParams.railingHeight;
    for (let i = 0; i < this._products.length; i++) {
      if (
        this._products[i].railingType &&
        this._products[i].railingType.includes(this._currentParams.railingType) &&
        this._products[i].productType === 'Horizontal Cable For Double Corner Whithout Baserail' &&
        this._products[i].heightGroup === heightValue
      ) {
        return this._products[i];
      }
    }
    const error = `railing type: ${this._currentParams.railingType},
      height: ${heightValue}`;

    this.showToast('error', 'Horizontal cable product is missing', error);
  }

  getCurrentSingleHorizontalCable(): any {
    this._currentParams = this.dataMenuService.getCurrentParams();

    for (let i = 0; i < this._products.length; i++) {
      if (
        this._products[i].railingType &&
        this._products[i].railingType.includes(this._currentParams.railingType) &&
        this._products[i].productType === 'Single Horizontal Cable For Stairs'
      ) {
        return this._products[i];
      }
    }
    const error = `railing type: ${this._currentParams.railingType},
      height: ${this._currentParams.railingHeight}`;

    this.showToast('error', 'Horizontal cable product is missing', error);
  }

  getCurrentInfillProduct(infillType: string): any {
    this._currentParams = this.dataMenuService.getCurrentParams();
    for (let i = 0; i < this._products.length; i++) {
      if (
        this._products[i].railingType &&
        this._products[i].railingType.includes(this._currentParams.railingType) &&
        this._products[i].productType === ProductTypes[infillType]
      ) {
        return this._products[i];
      }
    }

    const error = `railing type: ${this._currentParams.railingType}`;

    this.showToast('error', `${ProductTypes[infillType]} is missing`, error);
  }

  getCurrentVerticalPicket(): any {
    const { railingHeight, railingType } = this.dataMenuService.getCurrentParams();
    const product = this._products.find(
      p =>
        p.railingType &&
        p.railingType.includes(railingType) &&
        p.productType === 'Vertical Picket' &&
        p.heightGroup === railingHeight &&
        p.verticalPicketShapeType === 'Square' &&
        p.verticalPicketForInfillWithBaserail
    );

    if (product) {
      return product;
    }

    const error = `railing type: ${railingType},
        height: ${railingHeight}`;

    this.showToast('error', 'Vertical Picket product is missing', error);
  }

  getCurrentHorizontalCableVerticalPicket(): any {
    let {
      railingHeight,
      railingType,
      verticalPicketShapeType,
      infill,
      postMounting
    } = this.dataMenuService.getCurrentParams();

    if (!verticalPicketShapeType) verticalPicketShapeType = 'Square';
    const infillWithBaserail = infill === 'horizontalCableBaserail';
    const fitsPostMount = picket => {
      if (infillWithBaserail) {
        return true;
      } else {
        return (
          picket.mountType === postMounting ||
          (picket.mountType === 'Base Mount' &&
            (postMounting === 'Stantion Mount' || postMounting === 'Core Mount'))
        );
      }
    };
    const product = this._products.find(
      p =>
        p.railingType &&
        p.railingType.includes(railingType) &&
        p.productType === 'Vertical Picket' &&
        p.heightGroup === railingHeight &&
        p.verticalPicketShapeType === verticalPicketShapeType &&
        Boolean(p.verticalPicketForInfillWithBaserail) == infillWithBaserail &&
        fitsPostMount(p)
    );

    if (product) {
      return product;
    }

    const error = `railing type: ${railingType},
      height: ${railingHeight}`;

    this.showToast('error', 'Vertical Picket product is missing', error);
  }

  getCurrentVerticalCable(height?): any {
    this._currentParams = this.dataMenuService.getCurrentParams();
    const heightValue = height ? height : this._currentParams.railingHeight;
    for (let i = 0; i < this._products.length; i++) {
      if (
        this._products[i].railingType &&
        this._products[i].railingType.includes(this._currentParams.railingType) &&
        this._products[i].productType === 'Vertical Cable' &&
        this._products[i].heightGroup === heightValue
      ) {
        return this._products[i];
      }
    }

    const error = `railing type: ${this._currentParams.railingType},
      height: ${heightValue}`;

    this.showToast('error', 'Vertical Cable product is missing', error);
  }

  getCurrentPostCapForCornerPost(): any {
    const post = this.getCurrentCornerPost();

    if (post) {
      for (let i = 0; i < this._products.length; i++) {
        if (this._products[i].id === post.postCap) {
          return this._products[i];
        }
      }
    }
  }

  getCurrentPostCapForIntermidiatePost(): any {
    const post = this.getCurrentIntermidiatePost();

    if (post) {
      for (let i = 0; i < this._products.length; i++) {
        if (this._products[i].id === post.postCap) {
          return this._products[i];
        }
      }
    }
  }

  getAccessories(): Promise<any> {
    return new Promise((resolve, reject) => {
      this._productsObservable.subscribe(prods => {
        // console.log('prods', prods);
        // this subscription sees the RAW catalog — apply the dock-props
        // placeholder merge here too (skipped once the backend has them)
        this._accessories = mergeLocalDockProps(prods).filter(prod => prod.productType === 'Accessory');
        resolve(this._accessories);
      });
    });
  }

  getDoorsAndWindows(): Promise<any> {
    return new Promise((resolve, reject) => {
      this._productsObservable.subscribe(prods => {
        const fromApi = (prods || []).filter(
          prod => prod.productType === 'Door' || prod.productType === 'Window'
        );
        this._doorsAndWindows = mergeGenericDoorsWindows(fromApi);
        resolve(this._doorsAndWindows);
      });
    });
  }

  getTrellis(): Promise<any> {
    return new Promise((resolve, reject) => {
      this._productsObservable.subscribe(prods => {
        const fromApi = (prods || []).filter(prod => prod.productType === 'Trellis');
        resolve(mergeLocalTrellis(fromApi));
      });
    });
  }

  getAwnings(): Promise<any> {
    return new Promise((resolve, reject) => {
      if (this._products && this._products.length) {
        resolve(this.awningService.getAwningEntities(this._products));
      } else {
        this._productsObservable.subscribe(products => {
          resolve(this.awningService.getAwningEntities(products));
        });
      }
    });
  }

  getBaseAwnings(): Promise<any> {
    return new Promise((resolve, reject) => {
      if (this._products && this._products.length) {
        resolve(this.awningService.getBaseAwningEntities(this._products));
      } else {
        this._productsObservable.subscribe(products => {
          resolve(this.awningService.getBaseAwningEntities(products));
        });
      }
    });
  }

  getExtensionAwning(baseAwning: AwningTypes, awningPosition: 'left' | 'right'): Promise<any> {
    return new Promise((resolve, reject) => {
      if (this._products && this._products.length) {
        resolve(
          this.awningService.getExtensionAwningEntity(this._products, baseAwning, awningPosition)
        );
      } else {
        this._productsObservable.subscribe(products => {
          resolve(
            this.awningService.getExtensionAwningEntity(products, baseAwning, awningPosition)
          );
        });
      }
    });
  }

  getStairHandrails(type: string): any {
    this._currentParams = this.dataMenuService.getCurrentParams();

    for (let i = 0; i < this._products.length; i++) {
      if (this._products[i].productType === type) {
        return this._products[i];
      }
    }

    const error = `railing type: ${this._currentParams.railingType}`;

    this.showToast('error', 'Stair Handrails product is missing', error);
  }
}



// WEBPACK FOOTER //
// ./src/app/services/products.service.ts